import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EMPTY, Observable, timer } from 'rxjs';
import { expand, map, switchMap } from 'rxjs/operators';
import { enviroment } from 'src/environments/environment';
import { VideoPlayback, VideoPlaybackSessionResponse } from 'src/app/model/response/video-playback-response';

/**
 * Sessão de reprodução HLS — `POST /video/{video_id}/playback-session`. Contrato completo em
 * docs/frontend-hls-contract.md e, do lado servidor, Api FTCloud/docs/backend-hls-contract.md.
 *
 * Endpoint dedicado: só ele emite os cookies assinados do CloudFront, por isso nunca é chamado
 * ao montar feed/lista — apenas quando um player vai realmente tocar o vídeo.
 */
@Injectable({ providedIn: 'root' })
export class VideoPlaybackService {
  /** Contrato: default quando `retry_after_seconds` vem ausente/inválido. */
  private static readonly DEFAULT_RETRY_SECONDS = 2;
  /** Contrato: após 180s sem status terminal, para de tentar (sem fabricar FAILED local). */
  private static readonly POLL_TIMEOUT_MS = 180_000;

  private readonly base = `${enviroment.BASE_URL_API}/video`;

  constructor(private http: HttpClient) {}

  /** Uma única chamada de sessão. Corpo vazio; os cookies do CloudFront exigem `withCredentials`. */
  createSession(videoId: string): Observable<VideoPlaybackSessionResponse> {
    return this.http.post<VideoPlaybackSessionResponse>(
      `${this.base}/${videoId}/playback-session`,
      {},
      { withCredentials: true }
    );
  }

  /**
   * Polling da máquina de estados do contrato: PENDING/RUNNING repetem a chamada respeitando
   * `retry_after_seconds`; a stream completa ao chegar em READY/FAILED, ou erra (sem retry) em
   * qualquer falha HTTP da sessão — o contrato manda parar em 401/403/404 e não define retry
   * para os demais status, então qualquer erro simplesmente propaga e encerra o polling.
   *
   * Implementado com `expand` em vez de `setInterval`: cada novo poll nasce de dentro do
   * anterior, então cancelar a subscription (ex.: `ngOnDestroy` de quem assina) derruba o timer
   * pendente e nenhuma chamada nova acontece — não há um intervalo "solto" por fora do
   * Observable para alguém esquecer de limpar.
   *
   * Se passar de 180s sem status terminal, a stream completa e o ÚLTIMO `playback` emitido
   * continua PENDING/RUNNING (nunca é fabricado um FAILED aqui) — cabe a quem assina notar que
   * `complete()` chegou sem status terminal e oferecer atualização manual.
   */
  pollSession(videoId: string): Observable<VideoPlayback> {
    const deadline = Date.now() + VideoPlaybackService.POLL_TIMEOUT_MS;

    return this.createSession(videoId).pipe(
      map((res) => res.playback),
      expand((playback) => {
        if (this.isTerminal(playback.status) || Date.now() >= deadline) {
          return EMPTY;
        }
        const delayMs = this.resolveRetrySeconds(playback.retry_after_seconds) * 1000;
        return timer(delayMs).pipe(
          switchMap(() => this.createSession(videoId)),
          map((res) => res.playback)
        );
      })
    );
  }

  private isTerminal(status: VideoPlayback['status']): boolean {
    return status === 'READY' || status === 'FAILED';
  }

  private resolveRetrySeconds(seconds: number | null | undefined): number {
    if (typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0) {
      return seconds;
    }
    return VideoPlaybackService.DEFAULT_RETRY_SECONDS;
  }
}
